import { createContext, useCallback, useMemo, useEffect, useRef, useState } from "react";
import { Maximize2, X } from "lucide-react";

/**
 * #1193 (D236) — the phone maps' full-screen control, written once for both
 * maps (Log a match's ground step, Grounds near you).
 *
 * THE SAME MAP, NOT A COPY. A second `MapContainer` in an overlay would be a
 * second leaflet instance with its own view, its own tiles and its own pin
 * handlers, and "picking a pin in full screen does what it does inline"
 * would be two code paths kept in step by hand. Instead the one map stays
 * mounted and its wrapper changes box: a `height` placeholder keeps the page
 * layout where it was, and the inner box goes from `absolute / inset 0`
 * to `fixed / inset 0` above the top bar. Nothing remounts, so the view, the
 * basemap choice and every `onPick` survive the trip.
 *
 * HISTORY. Opening pushes one history entry carrying `nwMapFs`, so phone Back
 * closes the overlay instead of leaving the screen. Every other close
 * (button, Escape, a pin pick) goes through `history.back()` too, so the
 * stack is left exactly as it was found. The pushed entry copies the current
 * `history.state` first: react-router keeps its position index there.
 *
 * LAYOUT. The box is a column: when open, a top strip of its own holds "Close
 * map" (nothing else is ever drawn under it), and the map area fills the rest.
 * Inline, the map area is the whole box and the 44px button sits in its
 * top-right. The map area is `[data-map-area]`, the scope `readControlRects`
 * (`lib/mapFit.js`) searches, so pin avoidance sees the button.
 *
 * MODAL. While open, Tab and Shift+Tab cycle inside the box and every sibling
 * branch outside it is made `inert` (including one that mounts after the
 * open), so focus and assistive tech cannot reach the page the overlay hides.
 * Navigation history follows the entry: the overlay is open exactly when the
 * current entry carries `nwMapFs`, so browser Forward onto a closed overlay's
 * entry re-opens it, and a page that mounts on a stale `nwMapFs` entry clears
 * the flag.
 *
 * G1 (D224, D236.3). The button is a map control, like zoom and attribution:
 * it is not a place to go and is not counted.
 */
export const MapFullScreenContext = createContext(null);

const FS_STATE_KEY = "nwMapFs";
export const MAP_FULLSCREEN_CONTROL_CLASS = "nw-map-fs-control";
const OVERLAY_Z = 1100; // above the top bar (z 20), leaflet's panes and controls (<= 1000)
const TAP = "var(--nw-size-tap, 44px)";

export const MAP_HEIGHT = "clamp(220px, 40vh, 360px)";

const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

const visibleStop = (el) => {
  const st = window.getComputedStyle(el);
  return st.display !== "none" && st.visibility !== "hidden";
};

const TOASTER = '[data-sonner-toaster], section[aria-label^="Notifications"]';
/** Sonner renders `<section aria-label="Notifications alt+T">` at rest and the toast list only while a toast shows. */
const holdsToaster = (el) => el.matches(TOASTER) || Boolean(el.querySelector(TOASTER));

/** Marks every sibling branch between `box` and `<body>` inert, and keeps doing
 * so while open: a branch that mounts after the overlay opened (Grounds near
 * you's "played nowhere" line lands when `/venues/played` answers, which on a
 * loaded machine is after the open) is caught by a MutationObserver on each
 * ancestor. Returns a release that lifts inert from what it still owns: a
 * node whose `inert` other code sets or removes while the map is open is
 * disowned and left as that code left it (PR #1273 F2). */
function makeRestInert(box) {
  const made = new Set();
  const chain = new Set();
  const parents = [];
  let observer = null;
  const mark = (sib) => {
    if (chain.has(sib) || sib.hasAttribute("inert") || holdsToaster(sib)) return;
    if (["SCRIPT", "STYLE", "LINK"].includes(sib.tagName)) return;
    sib.setAttribute("inert", "");
    made.add(sib);
    // Watched only after our own write, so any record here is someone else's.
    observer?.observe(sib, { attributes: true, attributeFilter: ["inert"] });
  };
  const handle = (records) => {
    records.forEach((r) => {
      if (r.type === "attributes") {
        made.delete(r.target);
        return;
      }
      r.addedNodes.forEach((n) => {
        if (n.nodeType === Node.ELEMENT_NODE && n.parentElement === r.target) mark(n);
      });
    });
  };
  observer = new MutationObserver(handle);
  let el = box;
  while (el && el !== document.body && el.parentElement) {
    chain.add(el);
    parents.push(el.parentElement);
    el = el.parentElement;
  }
  parents.forEach((parent) => [...parent.children].forEach(mark));
  parents.forEach((parent) => observer.observe(parent, { childList: true }));
  return () => {
    handle(observer.takeRecords());
    observer.disconnect();
    made.forEach((sib) => sib.removeAttribute("inert"));
  };
}

const inFullScreenEntry = () => Boolean(window.history.state && window.history.state[FS_STATE_KEY]);

export default function MapFullScreen({ testId, children, controlInPill = false }) {
  const [open, setOpen] = useState(false);
  const openBtnRef = useRef(null);
  const closeBtnRef = useRef(null);
  const boxRef = useRef(null);
  const wasOpen = useRef(false);

  const openMap = useCallback(() => {
    window.history.pushState({ ...(window.history.state || {}), [FS_STATE_KEY]: true }, "");
    setOpen(true);
  }, []);

  // The open handler and the opener's ref travel in the context too: with `controlInPill` the map's own
  // control pill draws the button (`MapControlPill`, #1620) and focus still returns to it on close.
  const ctxValue = useMemo(() => ({ open, openMap, openBtnRef }), [open, openMap]);

  const close = useCallback(() => {
    if (inFullScreenEntry()) window.history.back();
    else setOpen(false);
  }, []);

  /** Close, and run `fn` once the history entry is gone, so a navigation `fn`
   * starts is not undone by the Back that is closing the overlay. */
  const closeThen = useCallback((fn) => {
    if (!open || !inFullScreenEntry()) {
      setOpen(false);
      fn();
      return;
    }
    let done = false;
    const run = () => {
      if (done) return;
      done = true;
      window.removeEventListener("popstate", run);
      fn();
    };
    window.addEventListener("popstate", run);
    window.setTimeout(run, 600);
    window.history.back();
  }, [open]);

  // A page that mounts on a stale `nwMapFs` entry (Back onto it from another
  // page, or a refresh while open) starts closed and clears the flag.
  useEffect(() => {
    if (inFullScreenEntry()) {
      const { [FS_STATE_KEY]: _flag, ...rest } = window.history.state;
      window.history.replaceState(rest, "");
    }
  }, []);

  // The overlay is open exactly when the current history entry is ours.
  useEffect(() => {
    const onPop = () => setOpen(inFullScreenEntry());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        close();
        return;
      }
      if (e.key !== "Tab") return;
      const root = boxRef.current;
      if (!root) return;
      const stops = [...root.querySelectorAll(FOCUSABLE)].filter((el) => !el.disabled && visibleStop(el));
      if (stops.length === 0) {
        e.preventDefault();
        return;
      }
      const first = stops[0];
      const last = stops[stops.length - 1];
      const active = document.activeElement;
      if (!root.contains(active) || (e.shiftKey && active === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const releaseInert = makeRestInert(boxRef.current);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      releaseInert();
    };
  }, [open, close]);

  // Focus goes into the overlay on open and back to the opening button on close.
  useEffect(() => {
    if (open) closeBtnRef.current?.focus();
    else if (wasOpen.current) openBtnRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  const controlBase = {
    position: "absolute",
    top: 8,
    right: 8,
    zIndex: 1001,
    minWidth: TAP,
    height: TAP,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    background: "var(--nw-surface)",
    color: "var(--nw-ink)",
    border: "1px solid var(--nw-line)",
    borderRadius: "var(--nw-radius-control)",
    boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
    cursor: "pointer",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    fontSize: 15,
    fontWeight: 600,
  };

  return (
    <MapFullScreenContext.Provider value={ctxValue}>
      <div
        data-testid={testId}
        style={{
          position: "relative",
          height: MAP_HEIGHT,
          borderRadius: "var(--nw-radius-card)",
          overflow: "hidden",
          border: "1px solid var(--nw-line)",
          // #1049 / #1194 (same class as #589, 7a3b29e): closed, the band is its
          // own stacking context, so the map notice (z-1000) and Leaflet's
          // controls cannot paint over a portal (dialog, menu, toast). Open,
          // the fixed box must keep its OVERLAY_Z (1100) against the top bar,
          // so the wrapper must not isolate it.
          isolation: open ? "auto" : "isolate",
        }}
      >
        <div
          ref={boxRef}
          data-testid={testId ? `${testId}-box` : undefined}
          data-map-fullscreen={open ? "open" : "closed"}
          role={open ? "dialog" : undefined}
          aria-modal={open ? "true" : undefined}
          aria-label={open ? "Map, full screen" : undefined}
          style={
            open
              ? { position: "fixed", inset: 0, zIndex: OVERLAY_Z, background: "var(--nw-surface)", display: "flex", flexDirection: "column" }
              : { position: "absolute", inset: 0, display: "flex", flexDirection: "column" }
          }
        >
          {open && (
            <div
              style={{
                flex: "0 0 auto",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                padding: "max(8px, env(safe-area-inset-top)) 8px 8px",
                borderBottom: "1px solid var(--nw-line)",
              }}
            >
              <button
                ref={closeBtnRef}
                type="button"
                className={MAP_FULLSCREEN_CONTROL_CLASS}
                data-testid="map-fullscreen-close"
                onClick={close}
                style={{ ...controlBase, position: "static", padding: "0 14px" }}
              >
                <X size={18} aria-hidden="true" />
                Close map
              </button>
            </div>
          )}
          <div data-map-area="" style={{ position: "relative", flex: "1 1 auto", minHeight: 0 }}>
            <div style={{ position: "absolute", inset: 0 }}>
              {typeof children === "function" ? children({ closeThen, open }) : children}
            </div>
            {!open && !controlInPill && (
              <button
                ref={openBtnRef}
                type="button"
                className={MAP_FULLSCREEN_CONTROL_CLASS}
                data-testid="map-fullscreen-open"
                aria-label="Show map full screen"
                onClick={openMap}
                style={controlBase}
              >
                <Maximize2 size={18} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </div>
    </MapFullScreenContext.Provider>
  );
}
